/**
 * <PageTranslateBar /> — bold, always-visible bar that translates ALL
 * user-generated content on the current page into the selected language.
 *
 * Behaviour:
 *   • Sits under the ExploreBar (top of every page).
 *   • Language selection persists in localStorage (`sp:page-translate-lang`).
 *   • When the user picks a language, we broadcast the `translate-page` event
 *     so every mounted <TranslatableText /> auto-translates in place.
 *   • Cancel × broadcasts `revert-page-translation` — cards show original.
 *   • Once picked, the bar keeps the language active until the user cancels
 *     or picks another one (satisfies the "persist till we change" ask).
 *
 * We don't fire any backend call from this bar itself — <TranslatableText />
 * handles the actual /api/i18n/translate calls (with sessionStorage cache).
 */
import { useEffect, useState } from "react";
import { Languages, X, ChevronDown, Check, Loader2 } from "lucide-react";
import { PREF_LANG_KEY, PREF_LANG_EVENT } from "@/components/LanguageChip";

// ISO-639-1 code → { name, native }. Curated list we can actually translate to.
const LANGS = [
  { code: "en", name: "English", native: "English" },
  { code: "es", name: "Spanish", native: "Español" },
  { code: "fr", name: "French", native: "Français" },
  { code: "de", name: "German", native: "Deutsch" },
  { code: "it", name: "Italian", native: "Italiano" },
  { code: "pt", name: "Portuguese", native: "Português" },
  { code: "ru", name: "Russian", native: "Русский" },
  { code: "hi", name: "Hindi", native: "हिन्दी" },
  { code: "mr", name: "Marathi", native: "मराठी" },
  { code: "bn", name: "Bengali", native: "বাংলা" },
  { code: "ta", name: "Tamil", native: "தமிழ்" },
  { code: "te", name: "Telugu", native: "తెలుగు" },
  { code: "ml", name: "Malayalam", native: "മലയാളം" },
  { code: "kn", name: "Kannada", native: "ಕನ್ನಡ" },
  { code: "gu", name: "Gujarati", native: "ગુજરાતી" },
  { code: "pa", name: "Punjabi", native: "ਪੰਜਾਬੀ" },
  { code: "ur", name: "Urdu", native: "اردو" },
  { code: "ar", name: "Arabic", native: "العربية" },
  { code: "fa", name: "Persian", native: "فارسی" },
  { code: "he", name: "Hebrew", native: "עברית" },
  { code: "zh", name: "Chinese", native: "中文" },
  { code: "ja", name: "Japanese", native: "日本語" },
  { code: "ko", name: "Korean", native: "한국어" },
  { code: "th", name: "Thai", native: "ไทย" },
  { code: "vi", name: "Vietnamese", native: "Tiếng Việt" },
  { code: "id", name: "Indonesian", native: "Bahasa Indonesia" },
  { code: "tr", name: "Turkish", native: "Türkçe" },
  { code: "sw", name: "Swahili", native: "Kiswahili" },
  { code: "uk", name: "Ukrainian", native: "Українська" },
  { code: "nl", name: "Dutch", native: "Nederlands" },
  { code: "pl", name: "Polish", native: "Polski" },
  { code: "el", name: "Greek", native: "Ελληνικά" },
];

export const PAGE_TRANSLATE_KEY = "sp:page-translate-lang";
export const PAGE_TRANSLATE_EVENT = "translate-page";
export const PAGE_REVERT_EVENT = "revert-page-translation";
export const PAGE_TRANSLATE_STATUS_EVENT = "page-translate-status";

function getStoredLang() {
  try { return localStorage.getItem(PAGE_TRANSLATE_KEY) || ""; } catch { return ""; }
}

export default function PageTranslateBar() {
  const [active, setActive] = useState(getStoredLang);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(0);      // pending TranslatableText count
  const [done, setDone] = useState(0);      // completed translations
  const [total, setTotal] = useState(0);    // total on the page

  // On mount: if a language was previously chosen, immediately request the
  // page to translate. Also broadcast whenever the browser fires a
  // storage event from another tab so language stays in sync.
  useEffect(() => {
    if (active) {
      // Delay one tick so newly mounted TranslatableText children register listeners.
      setTimeout(() => window.dispatchEvent(new CustomEvent(PAGE_TRANSLATE_EVENT, { detail: { lang: active } })), 0);
    }
  }, [active]);

  // Listen to status pings from TranslatableText instances so we can show
  // a live "translating X of Y" indicator.
  useEffect(() => {
    const onStatus = (e) => {
      const { pending, completed, total } = e.detail || {};
      setBusy(pending || 0);
      setDone(completed || 0);
      setTotal(total || 0);
    };
    window.addEventListener(PAGE_TRANSLATE_STATUS_EVENT, onStatus);
    return () => window.removeEventListener(PAGE_TRANSLATE_STATUS_EVENT, onStatus);
  }, []);

  const pick = (code) => {
    if (code === "en" && !active) {
      setOpen(false);
      return;
    }
    try { localStorage.setItem(PAGE_TRANSLATE_KEY, code); } catch { /* ignore */ }
    setActive(code);
    setOpen(false);
    // Also update the header LanguageChip pref so preference is unified
    // across the whole app.
    try { localStorage.setItem(PREF_LANG_KEY, code); } catch { /* ignore */ }
    try { window.dispatchEvent(new CustomEvent(PREF_LANG_EVENT, { detail: { code } })); } catch { /* ignore */ }
    // Broadcast to every mounted TranslatableText — they translate in place.
    try { window.dispatchEvent(new CustomEvent(PAGE_TRANSLATE_EVENT, { detail: { lang: code } })); } catch { /* ignore */ }
  };

  const cancel = () => {
    try { localStorage.removeItem(PAGE_TRANSLATE_KEY); } catch { /* ignore */ }
    setActive("");
    setOpen(false);
    try { window.dispatchEvent(new CustomEvent(PAGE_REVERT_EVENT)); } catch { /* ignore */ }
  };

  const activeLang = LANGS.find((l) => l.code === active);
  const pillLabel = activeLang
    ? (activeLang.native && activeLang.native !== activeLang.name ? `${activeLang.native} · ${activeLang.name}` : activeLang.name)
    : "Choose language";
  const progress = total > 0 && busy > 0
    ? `Translating ${Math.max(1, done + 1)} of ${total}…`
    : null;

  return (
    <div
      data-testid="page-translate-bar"
      className="w-full border-b"
      style={{
        background: active ? "var(--wofy-primary)" : "var(--wofy-paper, #FDFBF7)",
        borderColor: "var(--wofy-line)",
        color: active ? "#FFF7EB" : "var(--wofy-text)",
        transition: "background 0.2s ease",
      }}
    >
      <div className="max-w-7xl mx-auto flex flex-wrap items-center gap-2 px-4 py-2">
        <span
          className="inline-flex items-center gap-2 font-semibold text-sm uppercase tracking-wider"
          data-testid="page-translate-label"
        >
          <Languages className="w-4 h-4" />
          Translate
        </span>

        <div className="relative">
          <button
            type="button"
            onClick={() => setOpen((s) => !s)}
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-sm font-bold hover:shadow-sm"
            style={{
              borderColor: active ? "#FFF7EB" : "var(--wofy-primary)",
              background: active ? "rgba(255,247,235,0.15)" : "#FFF",
              color: active ? "#FFF7EB" : "var(--wofy-primary)",
            }}
            data-testid="page-translate-picker-btn"
            aria-haspopup="listbox"
            aria-expanded={open}
          >
            <span dir="auto">{pillLabel}</span>
            <ChevronDown className="w-3.5 h-3.5" />
          </button>
          {open && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
              <div
                className="absolute left-0 mt-1 z-50 rounded-xl shadow-xl border text-sm w-64 overflow-y-auto"
                style={{ background: "#FDFBF7", borderColor: "var(--wofy-border)", maxHeight: "60vh", color: "var(--wofy-text)" }}
                data-testid="page-translate-menu"
                role="listbox"
              >
                {LANGS.map((l) => {
                  const isActive = l.code === active;
                  return (
                    <button
                      key={l.code}
                      type="button"
                      onClick={() => pick(l.code)}
                      dir="auto"
                      className="w-full flex items-center justify-between px-3 py-1.5 text-left hover:bg-black/[0.04]"
                      style={{ background: "#FDFBF7" }}
                      data-testid={`page-translate-option-${l.code}`}
                      role="option"
                      aria-selected={isActive}
                    >
                      <span>
                        <span className="font-medium">{l.name}</span>
                        {l.native && l.native !== l.name && (
                          <span className="opacity-60 ml-2 text-xs">{l.native}</span>
                        )}
                      </span>
                      {isActive && <Check className="w-3.5 h-3.5" style={{ color: "var(--wofy-primary)" }} />}
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {active && (
          <button
            type="button"
            onClick={cancel}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-xs font-semibold hover:shadow-sm"
            style={{
              borderColor: "#FFF7EB",
              background: "rgba(255,247,235,0.15)",
              color: "#FFF7EB",
            }}
            data-testid="page-translate-cancel"
            aria-label="Cancel translation and show original"
            title="Cancel translation — show original content"
          >
            <X className="w-3.5 h-3.5" /> Cancel
          </button>
        )}

        <span
          className="ml-auto text-[11px] inline-flex items-center gap-1.5"
          style={{ color: active ? "rgba(255,247,235,0.85)" : "var(--wofy-text-soft)" }}
        >
          {progress ? (
            <>
              <Loader2 className="w-3 h-3 animate-spin" />
              {progress}
            </>
          ) : active ? (
            <>Translating this page automatically. Cancel to revert.</>
          ) : (
            <>Translate everything on this page into your language.</>
          )}
        </span>
      </div>
    </div>
  );
}
